import React, { useEffect, useMemo } from 'react';
import { Form, Typography, Button, Card, Divider, message } from 'antd';
import { SaveOutlined } from '@ant-design/icons';
import CommonForm from '../../../global/form';
import { useGenericMutation } from '../../../../queries/use-generic-mutation';
import SOURCES from '../../../../utils/constants';
import { cn } from '../../../../utils/cn';
import Spinner from '../../../global/spinner';

const { Title, Text } = Typography;

const EnhancedDJEventPlanner = ({ destinations, activeDestination, btnClassName }) => {
    const [form] = Form.useForm();
    const DESTINATION_DJEVENT = SOURCES.DESTINATION_DJ_EVENT_PLANNER || "djeventplanner";

    const { mutate: saveDJEvent, isPending: isSaving } = useGenericMutation({
        endpoint: "integration",
        method: "POST",
        onSuccess: () => message.success("DJ Event Planner configuration saved successfully")
    });

    useEffect(() => {
        if (destinations) {
            const config = destinations.find(d => d.integrationCompany === DESTINATION_DJEVENT);
            if (config) {
                form.setFieldsValue({
                    email: config.destinationAccountEmail,
                    password: config.destinationAccountPassword,
                    sendLeadsLimit: config.leadSentConfig?.sentLimit || 0,
                    standaloneDomain: config.publicKey
                });
            }
        }
    }, [destinations, form]);

    const onFinish = (values) => {
        saveDJEvent({
            destinationAccountEmail: values.email,
            destinationAccountPassword: values.password,
            sendLeadsLimit: values.sendLeadsLimit,
            integrationCompany: DESTINATION_DJEVENT,
            publicKey: values.standaloneDomain,
            locations: [],
        });
    };

    const formData = useMemo(() => [
        {
            inputType: SOURCES.FORMINPUTTYPES.INPUT,
            label: "Account Email",
            name: "email",
            placeholder: "Enter DJ Event Planner Email",
            type: "email",
            required: true,
            span: 12,
        },
        {
            inputType: SOURCES.FORMINPUTTYPES.INPUT,
            label: "Account Password",
            name: "password",
            placeholder: "Enter DJ Event Planner Password",
            type: "password",
            required: true,
            span: 12,
            validate: false
        },
        {
            inputType: SOURCES.FORMINPUTTYPES.INPUT,
            label: "Initial Lead Send Limit",
            name: "sendLeadsLimit",
            placeholder: "Leads limit",
            type: "number",
            span: 12,
        },
        {
            inputType: SOURCES.FORMINPUTTYPES.INPUT,
            label: "Standalone Domain",
            name: "standaloneDomain",
            placeholder: "Domain integrated with DJEvent Planner (if any)",
            span: 12,
        }
    ], []);

    return (
        <div className="animate-in fade-in duration-500">
            <div className="flex items-center gap-4 mb-8">
                <div className="size-16 rounded-2xl bg-white shadow-sm border border-gray-100 flex items-center justify-center p-3">
                    <img src={activeDestination.icon} alt={activeDestination.title} className="w-full h-full object-contain" />
                </div>
                <div>
                    <Title level={3} className="!mb-1">{activeDestination.title} Configuration</Title>
                    <Text type="secondary">Manage your DJ Event Planner integration settings.</Text>
                </div>
            </div>

            <Card className="rounded-2xl border-none shadow-sm">
                <CommonForm
                    form={form}
                    data={formData}
                    onFinish={onFinish}
                    layoutType="vertical"
                />
                <Divider className="mb-4 border-gray-100" />
                <div className="flex justify-start">
                    <Button
                        size="large"
                        icon={isSaving ? null : <SaveOutlined />}
                        onClick={() => form.submit()}
                        className={cn(btnClassName)}
                    >
                        {isSaving && <Spinner className={"!border-white"} />}
                        Save Configuration
                    </Button>
                </div>
            </Card>
        </div>
    );
};

export default EnhancedDJEventPlanner;
